import React, { useMemo } from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CONTENT_SHAPE } from '../../constants/contentColors';
import { statusToneTokens, type StatusTone } from '../../constants/statusTokens';
import { ZERO_ICONS } from '../../constants/zeroIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import type { ContainerMapItem } from '../../types/dashboard.type';
import { containerTone, summarizeContainers } from '../../utils/containers.helpers';
import KpiFigure from '../metrics/KpiFigure';
import MetricStrip from '../metrics/MetricStrip';

const TONE_ORDER: StatusTone[] = ['failed', 'attention', 'pending', 'complete', 'offline'];
const TONE_LABEL: Record<string, string> = {
  failed: 'dashboard.containers.tone.failed',
  attention: 'dashboard.containers.tone.attention',
  pending: 'dashboard.containers.tone.pending',
  complete: 'dashboard.containers.tone.complete',
  offline: 'dashboard.containers.tone.offline',
};

/**
 * Container health at a glance: counts computed from the container list
 * itself (distinct containers with an alarm — the API's own counter
 * double-counts), fill vs capacity over containers with a known unit, and a
 * status distribution bar with a text legend.
 */
const ContainerHealth: React.FC<{ containers: ContainerMapItem[] }> = ({ containers }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const summary = useMemo(() => summarizeContainers(containers), [containers]);
  const distribution = useMemo(() => {
    const counts = new Map<StatusTone, number>();
    for (const c of containers) counts.set(containerTone(c), (counts.get(containerTone(c)) ?? 0) + 1);
    return TONE_ORDER.map((tone) => ({ tone, count: counts.get(tone) ?? 0 })).filter((d) => d.count > 0);
  }, [containers]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
      <MetricStrip columns={{ xs: 2, sm: 3, lg: 5 }}>
        <KpiFigure icon={ZERO_ICONS.container} label={t('dashboard.containers.total')} value={fmt.number(summary.total)} size="md" />
        <KpiFigure icon={ZERO_ICONS.attention} label={t('dashboard.containers.withAlarm')} value={fmt.number(summary.withAlarm)} size="md" />
        <KpiFigure icon={ZERO_ICONS.alarmFull} label={t('dashboard.containers.fullAlarm')} value={fmt.number(summary.fullAlarm)} size="md" />
        <KpiFigure icon={ZERO_ICONS.pending} label={t('dashboard.containers.awaiting')} value={fmt.number(summary.awaitingPickup)} size="md" />
        <KpiFigure
          icon={ZERO_ICONS.mass}
          label={t('dashboard.containers.fill')}
          value={summary.fillShare === null ? '—' : fmt.number(summary.fillShare * 100, { maximumFractionDigits: 0 })}
          unit={summary.fillShare === null ? undefined : '%'}
          size="md"
          context={summary.fillShare === null ? t('dashboard.containers.fillUnknown') : t('dashboard.containers.fillHint')}
        />
      </MetricStrip>

      {summary.total > 0 ? (
        <Box>
          <Box
            role="img"
            aria-label={distribution
              .map((d) => `${t(TONE_LABEL[d.tone])}: ${fmt.number(d.count)}`)
              .join(', ')}
            sx={{ display: 'flex', height: 8, borderRadius: `${CONTENT_SHAPE.xs}px`, overflow: 'hidden', gap: '2px' }}
          >
            {distribution.map((d) => (
              <Tooltip key={d.tone} title={`${t(TONE_LABEL[d.tone])}: ${fmt.number(d.count)}`}>
                <Box
                  sx={{
                    flex: `${d.count} 1 0`,
                    bgcolor: statusToneTokens(theme.palette.mode, d.tone).fill,
                    animation: 'zero-fade-in 600ms ease-out',
                  }}
                />
              </Tooltip>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 0.5, mt: 1 }}>
            {distribution.map((d) => (
              <Box key={d.tone} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  aria-hidden
                  sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: statusToneTokens(theme.palette.mode, d.tone).fill }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t(TONE_LABEL[d.tone])}{' '}
                  <Box component="bdi" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {fmt.number(d.count)}
                  </Box>
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default ContainerHealth;
